/**
 * Step-up authentication. Sensitive account changes answer 403 REAUTH_REQUIRED when the session's
 * last password or passkey check is older than a few minutes. The request layer then asks the
 * registered handler (the step-up dialog) to confirm the user, and retries once on success.
 */
/** "passkey": the action accepts only a user-verified passkey (PASSKEY_REAUTH_REQUIRED). */
export type StepUpKind = "any" | "passkey";

export type StepUpHandler = (kind: StepUpKind) => Promise<boolean>;

let handler: StepUpHandler | undefined;
let pending: Promise<boolean> | undefined;

/** Registers the dialog; returns the unregister function. Only one handler is active. */
export function setStepUpHandler(next: StepUpHandler): () => void {
  handler = next;
  return (): void => {
    if (handler === next) {
      handler = undefined;
    }
  };
}

/** True once the user confirmed. Parallel requests share one prompt. */
export async function requestStepUp(kind: StepUpKind = "any"): Promise<boolean> {
  const current = handler;
  if (current === undefined) {
    return false;
  }
  if (pending === undefined) {
    pending = current(kind).finally((): void => {
      pending = undefined;
    });
  }
  return pending;
}
